import XDate from 'xdate'

import {
  ChargeSettlement,
  MaintenanceRequestPriority,
  MaintenanceRequestStatus,
  ResidentLedgerEntry,
  ResidentLedgerEntryStatus,
  ResidentLedgerEntryType,
  ResidentOfflinePaymentMethod,
  ResidentTenancyStatus,
} from '../_types/resident'

type LedgerStatusTone = 'settled' | 'pending' | 'failed'

const TENANCY_STATUS_LABELS: Record<ResidentTenancyStatus, string> = {
  invited: 'Invited',
  active: 'Active',
  past: 'Past',
  evicted: 'Ended',
  cancelled: 'Cancelled',
}

const LEDGER_TYPE_LABELS: Record<ResidentLedgerEntryType, string> = {
  charge: 'Charge',
  payment: 'Payment',
  credit: 'Credit',
  adjustment: 'Adjustment',
}

const OFFLINE_METHOD_LABELS: Record<ResidentOfflinePaymentMethod, string> = {
  cash: 'cash',
  'e-transfer': 'e-transfer',
  cheque: 'cheque',
  'money-order': 'money order',
  other: 'another method',
}

const LEDGER_STATUS_LABELS: Record<ResidentLedgerEntryStatus, string> = {
  scheduled: 'Scheduled',
  pending: 'Pending',
  settled: 'Settled',
  failed: 'Failed',
  reversed: 'Reversed',
}

const MAINTENANCE_PRIORITY_LABELS: Record<MaintenanceRequestPriority, string> =
  {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    emergency: 'Emergency',
  }

/**
 * Formats a cents amount as a localised dollar string.
 *
 * @param {number} cents - the amount in cents
 * @returns {string} e.g. "$2,000.00"
 */
const formatCents = (cents: number): string => {
  const dollars = (cents / 100).toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `$${dollars}`
}

/**
 * Maps a tenancy status to its renter-facing label.
 *
 * @param {ResidentTenancyStatus} tenancyStatus - the tenancy status
 * @returns {string} the human-readable label
 */
const formatTenancyStatus = (tenancyStatus: ResidentTenancyStatus): string =>
  TENANCY_STATUS_LABELS[tenancyStatus]

/**
 * Maps a ledger entry type to its display label.
 *
 * @param {ResidentLedgerEntryType} entryType - the ledger entry type
 * @returns {string} the human-readable label
 */
const formatLedgerType = (entryType: ResidentLedgerEntryType): string =>
  LEDGER_TYPE_LABELS[entryType]

/**
 * Whether the property manager recorded this entry as money that arrived outside
 * the app, rather than a payment the app processed.
 *
 * Tested positively: entries written before offline payments existed carry no
 * `offlineMethod` at all, and a `!== null` check would read their `undefined` as
 * offline and mislabel every historical payment.
 *
 * @param {ResidentLedgerEntry} entry - the ledger entry
 * @returns {boolean} true when it was recorded rather than processed
 */
const isRecordedOutsideApp = (entry: ResidentLedgerEntry): boolean =>
  typeof entry.offlineMethod === 'string'

/**
 * The renter-facing label for where an entry came from.
 *
 * Naming the method matters more to the renter than to anyone else: it is their
 * confirmation that the cash they handed over in person was actually written down.
 *
 * @param {ResidentLedgerEntry} entry - the ledger entry
 * @returns {string} the label for the entry
 */
const formatLedgerSource = (entry: ResidentLedgerEntry): string => {
  if (isRecordedOutsideApp(entry) === false) {
    return LEDGER_TYPE_LABELS[entry.type]
  }

  return `Payment by ${
    OFFLINE_METHOD_LABELS[entry.offlineMethod as ResidentOfflinePaymentMethod]
  }`
}

/**
 * The acknowledgement line for a payment the property manager recorded by hand,
 * or an empty string for anything the app processed itself.
 *
 * This is the part a renter paying cash has never had: a dated, written
 * confirmation from their landlord that the money was received.
 *
 * @param {ResidentLedgerEntry} entry - the ledger entry
 * @returns {string} the acknowledgement, or '' when not applicable
 */
const formatLedgerAcknowledgement = (entry: ResidentLedgerEntry): string => {
  if (isRecordedOutsideApp(entry) === false) {
    return ''
  }

  const receivedOn =
    typeof entry.receivedOn === 'number' ? entry.receivedOn : entry.createdAt

  return `Recorded by your property manager as received on ${formatLedgerDate(
    receivedOn,
  )}`
}

/**
 * Maps a ledger entry status to its display label.
 *
 * @param {ResidentLedgerEntryStatus} status - the ledger entry status
 * @returns {string} the human-readable label
 */
const formatLedgerStatus = (status: ResidentLedgerEntryStatus): string =>
  LEDGER_STATUS_LABELS[status]

/**
 * Maps a ledger entry status to a pill tone: settled (paid/cleared), failed
 * (failed/reversed), or pending (scheduled/in-flight).
 *
 * @param {ResidentLedgerEntryStatus} status - the ledger entry status
 * @returns {LedgerStatusTone} the tone for the status pill
 */
const getLedgerStatusTone = (
  status: ResidentLedgerEntryStatus,
): LedgerStatusTone => {
  if (status === 'settled') {
    return 'settled'
  }
  if (status === 'failed' || status === 'reversed') {
    return 'failed'
  }
  return 'pending'
}

/**
 * Formats a millisecond timestamp as a short date using XDate.
 *
 * @param {number} milliseconds - the timestamp in milliseconds
 * @returns {string} e.g. "Jun 6, 2026"
 */
const formatLedgerDate = (milliseconds: number): string =>
  new XDate(new Date(milliseconds)).toString('MMM d, yyyy')

/**
 * The supporting line under one outstanding charge: when it is due, whether
 * anything has been paid toward it, and whether a payment is still clearing.
 *
 * A charge with money in flight says so plainly and loses its Pay button. A
 * resident who paid by bank on Monday sees why they are not being asked again,
 * rather than seeing an unchanged balance and paying a second time.
 *
 * @param {ChargeSettlement} charge - the charge to describe
 * @returns {string} the meta line, which is never empty
 */
const formatChargeMeta = (charge: ChargeSettlement): string => {
  const parts: string[] = []

  if (charge.dueDate !== null) {
    parts.push(`Due ${formatLedgerDate(charge.dueDate)}`)
  }

  if (charge.pendingCents > 0) {
    parts.push(`${formatCents(charge.pendingCents)} clearing`)
  }

  if (charge.paidCents > 0) {
    parts.push(`${formatCents(charge.paidCents)} paid of ${formatCents(charge.amountCents)}`)
  }

  if (parts.length === 0) {
    return 'Outstanding'
  }

  return parts.join(' - ')
}

/**
 * Maps a maintenance priority to its display label.
 *
 * @param {MaintenanceRequestPriority} priority - the request priority
 * @returns {string}
 */
const formatMaintenancePriority = (
  priority: MaintenanceRequestPriority,
): string => MAINTENANCE_PRIORITY_LABELS[priority]

/**
 * Maps a maintenance status to its display label.
 *
 * @param {MaintenanceRequestStatus} maintenanceStatus - the request status
 * @returns {string}
 */
const formatMaintenanceStatus = (
  maintenanceStatus: MaintenanceRequestStatus,
): string => {
  switch (maintenanceStatus) {
    case 'submitted': {
      return 'Submitted'
    }
    case 'acknowledged': {
      return 'Acknowledged'
    }
    case 'in-progress': {
      return 'In progress'
    }
    case 'resolved': {
      return 'Resolved'
    }
    case 'closed': {
      return 'Closed'
    }
    default: {
      return maintenanceStatus
    }
  }
}

export {
  formatCents,
  formatChargeMeta,
  formatTenancyStatus,
  formatLedgerType,
  formatLedgerSource,
  formatLedgerAcknowledgement,
  isRecordedOutsideApp,
  formatLedgerStatus,
  getLedgerStatusTone,
  formatLedgerDate,
  formatMaintenancePriority,
  formatMaintenanceStatus,
}
